import { Link } from 'react-router-dom';

import { useUserProfile, loyaltyTierLabel } from '@/hooks/useUserProfile';
import { useMembershipPlans } from '@/hooks/useMembershipPlans';
import { Badge, Button, Card, CardTitle, toast } from '@/components/ui';
import { formatEventDate } from '@/lib/format';
import {
  cycleLabel,
  daysUntilExpiry,
  formatExpiryDate,
  lifecycleSeverity,
} from '@/lib/membershipLifecycle';

/**
 * AccountMembership — `/account/membership`. Estado de la membresía del
 * usuario, leído de `public.user_profiles` (ROUND8 UAT-C05). Si hay una
 * suscripción activa muestra el plan + beneficios; si no, un estado digno
 * con la categoría del usuario y CTA a explorar planes. ROUND14: rename
 * "tier"/"nivel de fidelidad" → "categoría" en copy visible.
 *
 * "Cancelar suscripción" dispara confirm + toast — la baja real (prorrateo
 * + gateway recurring) entra en un sprint posterior.
 */
export function AccountMembership(): JSX.Element {
  const { data: profile, isLoading, isError } = useUserProfile();
  const { data: plans } = useMembershipPlans();

  if (isLoading) {
    return (
      <Card variant="default" padded aria-busy="true">
        <div className="h-5 w-24 rounded bg-neutral-100" />
        <div className="mt-3 h-6 w-40 rounded bg-neutral-100" />
        <div className="mt-4 h-4 w-56 rounded bg-neutral-100" />
      </Card>
    );
  }

  if (isError || !profile) {
    return (
      <Card variant="outlined" padded>
        <CardTitle>No pudimos cargar tu membresía</CardTitle>
        <p className="mt-2 text-sm text-fc-muted" role="alert">
          Ocurrió un error al cargar el estado de tu membresía. Probá recargar la página.
        </p>
      </Card>
    );
  }

  const tierLabel = loyaltyTierLabel(profile.loyaltyTier);
  const activePlan =
    profile.subscriptionActive && profile.subscriptionPlan
      ? (plans?.find(
          (p) =>
            p.slug === profile.subscriptionPlan ||
            p.name.toLowerCase() === profile.subscriptionPlan?.toLowerCase(),
        ) ?? null)
      : null;

  function handleCancel(): void {
    if (
      !confirm(
        '¿Querés cancelar tu suscripción? Mantendrás los beneficios hasta el final del período pago.',
      )
    ) {
      return;
    }
    toast.info({
      title: 'Solicitud de cancelación enviada',
      description: 'Te confirmaremos por email los siguientes pasos.',
    });
  }

  return (
    <div className="flex flex-col gap-6">
      {profile.subscriptionActive ? (
        <Card variant="default" padded>
          <div className="flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <Badge tone="gold">{activePlan?.name ?? profile.subscriptionPlan ?? 'Plan'}</Badge>
              <CardTitle className="mt-2">Tu plan actual</CardTitle>
              <p className="mt-1 text-sm text-fc-muted">
                Ciclo:{' '}
                <span className="font-medium text-fc-ink">
                  {cycleLabel(profile.subscriptionCycle)}
                </span>
                {profile.memberSince && (
                  <>
                    {' · '}Miembro desde {formatEventDate(profile.memberSince)}
                  </>
                )}
              </p>
            </div>
            <ExpiryBadge expiresAtIso={profile.subscriptionExpiresAt} />
          </div>

          {profile.subscriptionExpiresAt && (
            <dl className="mt-6 grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
              <div>
                <dt className="text-fc-muted">Vence el</dt>
                <dd className="mt-1 font-medium tabular-nums text-fc-ink">
                  {formatExpiryDate(profile.subscriptionExpiresAt)}
                </dd>
              </div>
              <div>
                <dt className="text-fc-muted">Renovación</dt>
                <dd className="mt-1 flex flex-wrap gap-2">
                  <Button
                    as={Link}
                    to={`/memberships/${profile.subscriptionPlan ?? activePlan?.slug ?? ''}?cycle=${profile.subscriptionCycle ?? 'monthly'}`}
                    variant="primary"
                    size="sm"
                  >
                    Renovar ahora
                  </Button>
                </dd>
              </div>
            </dl>
          )}
        </Card>
      ) : (
        <Card variant="outlined" padded>
          <CardTitle>No tenés una suscripción activa</CardTitle>
          <p className="mt-2 text-sm text-fc-muted">
            Estás usando First Class con el acceso base
            {tierLabel && (
              <>
                {' '}
                y tu <strong>Categoría FC</strong> es <strong>{tierLabel}</strong>
              </>
            )}
            . Activá un plan para desbloquear descuentos, preventas y experiencias VIP.
          </p>
          <Button as={Link} to="/memberships" variant="primary" size="sm" className="mt-4">
            Explorar planes
          </Button>
        </Card>
      )}

      <Card variant="ghost" padded>
        <CardTitle>Tu actividad</CardTitle>
        <dl className="mt-4 grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
          <div>
            <dt className="text-fc-muted">Categoría FC</dt>
            <dd className="mt-1 font-medium text-fc-ink">{tierLabel ?? 'Base'}</dd>
          </div>
          <div>
            <dt className="text-fc-muted">Puntos disponibles</dt>
            <dd className="mt-1 text-lg font-semibold tabular-nums text-fc-ink">
              {profile.points.toLocaleString('es-CO')} pts
            </dd>
          </div>
        </dl>
      </Card>

      {activePlan && activePlan.features.length > 0 && (
        <Card variant="ghost" padded>
          <CardTitle>Beneficios incluidos</CardTitle>
          <ul className="mt-4 grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
            {activePlan.features.map((f, i) => (
              <li key={i} className="flex items-start gap-2">
                <span
                  aria-hidden
                  className="mt-1.5 inline-block h-1.5 w-1.5 flex-none rounded-full bg-primary"
                />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {profile.subscriptionActive && (
        <Card variant="default" padded>
          <CardTitle>Gestionar plan</CardTitle>
          <p className="mt-2 text-sm text-fc-muted">
            Subí a un plan superior y desbloqueá más beneficios, o cambiá tu suscripción cuando
            quieras.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button as={Link} to="/memberships" variant="primary" size="sm">
              Comparar planes
            </Button>
            <Button variant="ghost" size="sm" onClick={handleCancel}>
              Cancelar suscripción
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

/**
 * ExpiryBadge — pill que cambia de color según los umbrales ROUND12:
 *  - ok       → verde "Activa"
 *  - soon (≤7d) → amarillo "Vence en N días"
 *  - critical (≤2d) → rojo "Vence en N días"
 *  - expired (≤0) → rojo "Expiró"
 */
function ExpiryBadge({ expiresAtIso }: { expiresAtIso: string | null }): JSX.Element {
  const days = daysUntilExpiry(expiresAtIso);
  const sev = lifecycleSeverity(days);
  if (sev === 'expired') {
    return <Badge tone="danger">Expiró</Badge>;
  }
  if (sev === 'critical' || sev === 'soon') {
    const label = days === 1 ? 'Vence mañana' : `Vence en ${days} días`;
    return <Badge tone={sev === 'critical' ? 'danger' : 'warning'}>{label}</Badge>;
  }
  return <Badge tone="success">Activa</Badge>;
}
